import { Button, Divider, Grid, Typography } from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { FiberManualRecordOutlined } from '@mui/icons-material';
import Transition from '../common/Transition';
import { GetPredictionResultDetails, handleTogglePredictionResultDetailsDialog } from '../../store/mltester-slice/mltester';
import moment from 'moment';
import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import { Header } from '../common/Header';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

const PredictionResultDetailsDialog = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { 
        predictionResultDetailsDialogOpen,
        predictionResultDetailsDialogBodyParams, 
        predictionResultDeatilsLoading, 
        predictionResultDetails
    } = useAppSelector((state: RootState) => state.mltester);

    function handleClose() {
        dispatch(handleTogglePredictionResultDetailsDialog({ isOpen: false, body: null }))
    }

    useEffect(() => {
        if(predictionResultDetailsDialogOpen)
            dispatch(GetPredictionResultDetails(predictionResultDetailsDialogBodyParams.id));
    }, [predictionResultDetailsDialogOpen])

    return(
        <Dialog
            fullWidth
            maxWidth="lg"
            open={predictionResultDetailsDialogOpen}
            TransitionComponent={Transition}
            keepMounted
            onClose={(_, reason) => {
                if(reason !== 'backdropClick') handleClose();
            }}
            aria-describedby="alert-dialog-slide-description"
        >
            <Header 
                title={t('PREDICTION_RESULT_DETAILS_TITLE', { name: predictionResultDetailsDialogBodyParams?.name })}
                helperIcon={
                    <RedirectIconButton to={'/help/6'} className="helper-icon" >
                        <ContactSupportIcon />
                    </RedirectIconButton>
                } 
            />
            <DialogContent>
                { !predictionResultDetails || predictionResultDeatilsLoading ? 
                <Grid container p={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    { predictionResultDeatilsLoading ? 
                        <Box sx={{ width: '100%' }}>
                            <LinearProgress />
                        </Box>
                    : !predictionResultDetails ? 
                        <Typography variant={'h6'}>{t('NO_PREDICTION_RESULT_DETAILS')}</Typography>
                    : '' }
                </Grid> : 
                <React.Fragment>
                    <Grid container sx={{ display: 'flex', flexDirection: 'row'}} spacing={10}>
                        <Grid item xs={6}>
                            <Grid mb={1} item style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <HelpOutlineIcon />
                                <Typography fontWeight="bold" ml={1} variant='h6'>{t('PREDICTION_RESULT_DETAILS_REQUEST')}</Typography>
                            </Grid>
                            <Divider />
                            <Grid mt={1.5} item xs={12}>
                                <Grid container display={"flex"} justifyContent={"space-between"}>
                                    <Grid item fontWeight="bold">{t('PREDICTION_RESULT_RESULT_NAME')}</Grid>
                                    <Grid item>{predictionResultDetails?.request?.mlResultName ?? '---'}</Grid>
                                </Grid>
                            </Grid>
                            <Grid item mt={1.5} xs={12}><Divider /></Grid>
                            <Grid container mt={1.5} display={"flex"} flexDirection={"column"}>
                                <Grid item xs={12}>
                                    <Grid item xs={12} fontWeight="bold">
                                        {t('PREDICTION_RESULT_INPUTS')}
                                    </Grid>
                                    <Grid item xs={12} sx={{ height: '199px', overflowY: 'auto' }}>
                                        { predictionResultDetails?.request?.inputColumns?.map((el : string, index: number) => {
                                            return(
                                                <Grid key={index} item xs={12} pr={1}>
                                                    <Grid container display={"flex"} justifyContent={"space-between"}>
                                                        <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row'}}>
                                                            <FiberManualRecordOutlined fontSize='small' />
                                                            <Typography ml={.5}>{el}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )
                                        })}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={6}>
                            <Grid mb={1} item style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <QuestionAnswerIcon />
                                <Typography fontWeight="bold" ml={1} variant='h6'>{t('PREDICTION_RESULT_DETAILS_RESPONSE')}</Typography>
                            </Grid>
                            <Divider />
                            <Grid container display={"flex"} flexDirection={"column"} spacing={1}>
                                <Grid item>
                                    <Grid mt={1.5} container display={"flex"} justifyContent={"space-between"}>
                                        <Grid item fontWeight="bold">{t('PREDICTION_SUCCESSFUL_RESULT')}</Grid>
                                        <Grid item>{predictionResultDetails?.response?.success ? <CheckCircleOutline sx={{ color: "green" }}/> : <CancelOutlined sx={{ color: 'red' }}/>}</Grid>
                                    </Grid>
                                </Grid>
                                <Grid item mt={-0.5}><Divider /></Grid>
                                <Grid item mt={0.5}>
                                    <Grid container display={"flex"} justifyContent={"space-between"}>
                                        <Grid item fontWeight="bold">{t('PREDICTION_START_DATE_TIME')}</Grid>
                                        <Grid item>{moment(predictionResultDetails?.response?.startPredictDateTimeUTC).format('DD/MM/YYYY hh:mm')}</Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Grid container display={"flex"} justifyContent={"space-between"}>
                                        <Grid item fontWeight="bold">{t('PREDICTION_END_DATE_TIME')}</Grid>
                                        <Grid item>{moment(predictionResultDetails?.response?.endPredictDateTimeUTC).format('DD/MM/YYYY hh:mm')}</Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Grid container display={"flex"} justifyContent={"space-between"}>
                                        <Grid item fontWeight="bold">{t('PREDICTION_DURATION')}</Grid>
                                        <Grid item>{predictionResultDetails?.response?.predictDuration}</Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </React.Fragment>
                }
            </DialogContent>
            <DialogActions>
                <Button className="button-text-color" onClick={handleClose}>{t('CLOSE_BUTTON')}</Button>
            </DialogActions> 
        </Dialog>
    )
}

export default PredictionResultDetailsDialog;